import { getChainIcon, getChainType, type SolanaChain } from '@particle-network/connector-core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RoutePath } from '..';
import AccountSecurityIcon from '../../assets/icons/AccountSecurityIcon';
import BuyIcon from '../../assets/icons/BuyIcon';
import DisconnectIcon from '../../assets/icons/DisconnectIcon';
import DownArrowIcon from '../../assets/icons/DownArrowIcon';
import ErrorIcon from '../../assets/icons/ErrorIcon';
import PlusIcon from '../../assets/icons/PlusIcon';
import ReceiveIcon from '../../assets/icons/ReceiveIcon';
import WalletIcon from '../../assets/icons/WalletIcon';
import ChainDropdown from '../../components/chainDropdown';
import CircleSpinner from '../../components/circleSpinner';
import { StyleSwitchNetwork } from '../../components/connectedWidget/styles';
import { FlexBetween, FlexCenter, FlexLeft, PageContent } from '../../components/modal/styles';
import RefreshIcon from '../../components/refreshIcon';
import { SocialItem } from '../../components/socialLogin/styles';
import { useAccount } from '../../hooks/useAccount';
import { useAddress } from '../../hooks/useAddress';
import { useAppearance } from '../../hooks/useAppearance';
import { useChains } from '../../hooks/useChains';
import { useDisconnect } from '../../hooks/useDisconnect';
import { useEmbeddedWallet } from '../../hooks/useEmbeddedWallet';
import useLocales from '../../hooks/useLocales';
import { useParticleAuth } from '../../hooks/useParticleAuth';
import { usePublicClient } from '../../hooks/usePublicClient';
import { useRouter } from '../../hooks/useRouter';
import { useWalletConnections } from '../../hooks/useWalletConnections';
import { useWallets } from '../../hooks/useWallets';
import { getBuyUrl, popupWindow } from '../../utils';
import { getChainDisplayName } from '../../utils/chains';
import { formatTokenAmount } from '../../utils/formatToken';
import {
  StyleActionIcon,
  StyleActionText,
  StyleActions,
  StyleBalance,
  StyleChainName,
  StyleDisconnectContent,
  StyleDisconnectLoading,
  StyleFooter,
  StyleFooterLabel,
  StyleModalContainer,
  StyleTextButton2,
  StyleWalletIcon,
  StyleWalletInfo,
  StyleWalletInfoLeft,
  StyleWalletInfoRight,
  StyleWalletText,
} from './styles';

const Page = () => {
  const chainList = useChains();
  const { chain, chainId: currentChainId, isConnected } = useAccount();
  const locales = useLocales();
  const chains = useChains();
  const [walletConnection] = useWalletConnections();
  const client = usePublicClient();
  const [primaryWallet] = useWallets();
  const { openAccountAndSecurity, openLinkLoginAccount, getUserInfo, openSetSecurityAccount } = useParticleAuth();
  const embeddedWallet = useEmbeddedWallet();
  const address = useAddress();
  const { close, navigate } = useRouter();
  const { disconnectAsync } = useDisconnect({
    onSettled(data, error) {
      console.log('wallet disconnect');
    },
  });

  const [refreshBalance, setRefreshBalance] = useState(false);
  const [chainDropdownVisible, setChainDropdownVisible] = useState(false);
  const [balance, setBalance] = useState<string | null>(null);
  const timer = useRef<NodeJS.Timeout>(undefined);
  const walletInfoLeftRef = useRef<HTMLDivElement>(null);
  const [disconnectLoading, setDisconnectLoading] = useState(false);

  const currentChainType = useMemo(() => {
    if (chainList && chainList?.length && currentChainId) {
      const chain = chainList.find((item) => item.id === currentChainId);
      if (chain) {
        return getChainType(chain);
      }
      return '';
    } else {
      return '';
    }
  }, [chainList, currentChainId]);

  /**
   * 是否支持当前链
   */
  const supportChain = useMemo(() => {
    return chains.some((item) => item.id === chain?.id);
  }, [chain, chains]);

  const getBalance = async () => {
    if (!currentChainType) {
      return;
    }
    const params =
      currentChainType === 'evm' ? { address } : primaryWallet?.getWalletClient?.<SolanaChain>()?.publicKey;
    if (address && params && client) {
      client
        .getBalance(params as any)
        .then((res) => {
          const { decimals = 1 } = chain?.nativeCurrency || {};

          const balance = formatTokenAmount(BigInt(res), decimals, 5) || '0';
          setBalance(balance);
          const key = `${chain?.id}-${address}-balance`?.toLowerCase();
          localStorage.setItem(key, balance);
        })
        .catch((e) => {
          //ignore
          console.log('get balance error', e);
        });
    }
  };

  const isParticle = useMemo(() => {
    return Boolean(walletConnection && walletConnection.walletProps.connector.walletConnectorType === 'particleAuth');
  }, [walletConnection]);

  const getCachedBalance = () => {
    const key = `${chain?.id}-${address}-balance`?.toLowerCase();
    return localStorage.getItem(key) || '0';
  };

  const { appearance } = useAppearance();

  const buyUrl = useMemo(() => {
    if (address) {
      return getBuyUrl(currentChainId, address, '', appearance.mode);
    }
    return undefined;
  }, [address, currentChainId, appearance.mode]);

  useEffect(() => {
    if (address && chain && client && primaryWallet) {
      setBalance(getCachedBalance());
      getBalance();
    }
  }, [address, client, chain, primaryWallet]);

  useEffect(() => {
    if (chain?.id && address) {
      setBalance(getCachedBalance());
    }
  }, [chain?.id, address, balance]);

  return (
    <PageContent>
      <StyleModalContainer>
        <StyleWalletInfo>
          <StyleWalletInfoLeft
            ref={walletInfoLeftRef}
            selected={chains?.length > 1 || !supportChain}
            onClick={() => {
              if (chains?.length > 1 || !supportChain) {
                setChainDropdownVisible(!chainDropdownVisible);
              }
            }}
          >
            {isConnected && (
              <>
                {supportChain ? (
                  <>
                    {chain && <StyleWalletIcon src={getChainIcon(chain)} alt='' />}
                    <StyleChainName>{getChainDisplayName(chain)}</StyleChainName>
                  </>
                ) : (
                  <StyleSwitchNetwork>
                    <ErrorIcon />
                    {locales.switchNetwork}
                  </StyleSwitchNetwork>
                )}

                {(chains?.length > 1 || !supportChain) && (
                  <StyleWalletText>
                    <DownArrowIcon
                      width={12}
                      height={12}
                      spin={!!chainDropdownVisible}
                      color={supportChain ? 'var(--pcm-body-color-secondary)' : 'var(--pcm-error-color)'}
                    />
                  </StyleWalletText>
                )}
              </>
            )}
          </StyleWalletInfoLeft>
          {supportChain && (
            <StyleWalletInfoRight>
              <StyleBalance>
                {balance || 0} &nbsp;
                {chain?.nativeCurrency.symbol?.replace('tBNB', 'BNB')}
              </StyleBalance>
              <RefreshIcon
                style={{ cursor: 'pointer' }}
                spin={refreshBalance}
                onClick={async () => {
                  clearTimeout(timer.current);
                  const start = new Date().getTime();
                  setRefreshBalance(true);
                  await getBalance();
                  const remain = 1000 - (new Date().getTime() - start);
                  timer.current = setTimeout(
                    () => {
                      setRefreshBalance(false);
                    },
                    remain > 0 ? remain : 0
                  );
                }}
              />
            </StyleWalletInfoRight>
          )}

          <ChainDropdown
            visible={chainDropdownVisible}
            setVisible={setChainDropdownVisible}
            clickAwayElements={[walletInfoLeftRef as any]}
            maxHeight={200}
            supportChain={supportChain}
            top={50}
          />
        </StyleWalletInfo>
        <StyleActions>
          <SocialItem
            $isDisabled={Boolean(!buyUrl)}
            $felxRow={false}
            onClick={() => {
              if (!buyUrl) return;

              const height = parseInt((window.innerHeight * 0.8).toString());
              let width = height * 0.6;
              if (width > 1000) {
                width = 1000;
              }
              if (width > window.innerWidth) {
                width = parseInt((window.innerWidth * 0.9).toString());
              }
              popupWindow(buyUrl, 'Particle Network', width, height);
              close();
            }}
          >
            <FlexCenter
              style={{
                width: '100%',
                gap: '6px',
              }}
            >
              <BuyIcon
                className={StyleActionIcon}
                style={{
                  width: '20px',
                }}
              />
              <StyleActionText>{locales.buy}</StyleActionText>
            </FlexCenter>
          </SocialItem>

          <SocialItem
            $felxRow={false}
            $isDisabled={Boolean(!address) || !chain}
            onClick={() => {
              navigate(RoutePath.Receive);
            }}
          >
            <FlexCenter
              style={{
                width: '100%',
                gap: '6px',
              }}
            >
              <ReceiveIcon
                className={StyleActionIcon}
                style={{
                  width: '18px',
                }}
              />
              <StyleActionText>{locales.receive}</StyleActionText>
            </FlexCenter>
          </SocialItem>

          {embeddedWallet && embeddedWallet.isCanOpen && (
            <SocialItem
              $felxRow={false}
              $isDisabled={Boolean(!address)}
              onClick={() => {
                embeddedWallet.openWallet();
                close();
              }}
            >
              <FlexCenter
                style={{
                  width: '100%',
                  gap: '6px',
                }}
              >
                <WalletIcon
                  className={StyleActionIcon}
                  style={{
                    width: '20px',
                  }}
                />
                <StyleActionText>{locales.wallet}</StyleActionText>
              </FlexCenter>
            </SocialItem>
          )}

          {isParticle && (
            <SocialItem
              $felxRow={false}
              onClick={() => {
                openAccountAndSecurity();
                close();
              }}
            >
              <FlexCenter
                style={{
                  width: '100%',
                  gap: '6px',
                }}
              >
                <AccountSecurityIcon
                  className={StyleActionIcon}
                  style={{
                    width: '20px',
                  }}
                />
                <StyleActionText>{locales.accountAndSecurity}</StyleActionText>
              </FlexCenter>
            </SocialItem>
          )}
        </StyleActions>
        <StyleFooter>
          <FlexBetween>
            {isParticle ? (
              <StyleTextButton2
                onClick={() => {
                  const useInfo = getUserInfo();
                  const { email, phone } = useInfo.security_account || {};
                  if (!email && !phone) {
                    openSetSecurityAccount();
                    close();
                  } else {
                    openLinkLoginAccount();
                    close();
                  }
                }}
              >
                <FlexLeft gap={5}>
                  <PlusIcon className={StyleActionIcon} />
                  <StyleFooterLabel>{locales.linkLoginAccount}</StyleFooterLabel>
                </FlexLeft>
              </StyleTextButton2>
            ) : (
              <div></div>
            )}

            <StyleTextButton2 $disconnectLoading={disconnectLoading}>
              <FlexLeft
                gap={5}
                onClick={() => {
                  setDisconnectLoading(true);
                  disconnectAsync().finally(() => {
                    setTimeout(() => {
                      setDisconnectLoading(false);
                    }, 300);
                  });
                }}
              >
                <StyleDisconnectContent>
                  {disconnectLoading && (
                    <StyleDisconnectLoading>
                      <CircleSpinner width={16} inset={0} height={16} logo={<></>} />
                    </StyleDisconnectLoading>
                  )}

                  <DisconnectIcon className={StyleActionIcon} />
                </StyleDisconnectContent>
                <StyleFooterLabel>{locales.disconnect}</StyleFooterLabel>
              </FlexLeft>
            </StyleTextButton2>
          </FlexBetween>
        </StyleFooter>
      </StyleModalContainer>
    </PageContent>
  );
};

export default Page;
